/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Dropdowns/AdminDropdownManager.tsx.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputLabel,
  FormControl,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { dropdownService } from "../../../../services/dropdownService";
import { Dropdown, DropdownOption } from "../../../../interfaces/dropdownInterfaces";
import "../Admin.css";
import "./AdminDropdownManager.css";

/** Toggle this flag to enable/disable dropdown editing (CRUD) in admin. */
const ENABLE_DROPDOWN_EDITING = false;

const AdminDropdownManager: React.FC = () => {
  const [dropdowns, setDropdowns] = useState<Dropdown[]>([]);
  const [selectedDropdownId, setSelectedDropdownId] = useState<number | "">("");
  const [newDropdown, setNewDropdown] = useState({ name: "", description: "" });
  const [openCreateDropdownDialog, setOpenCreateDropdownDialog] = useState(false);

  const [openOptionDialog, setOpenOptionDialog] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState<number | null>(null);
  const [newOption, setNewOption] = useState({
    label: "",
    value: "",
    DQI_Score: "",
  });

  // ✅ Confirmation dialogs
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<() => void>(() => () => {});

  // Fetch dropdowns
  const loadDropdowns = async () => {
    const data = await dropdownService.getAll();
    setDropdowns(data);
  };

  useEffect(() => {
    loadDropdowns();
  }, []);

  const openCreateDropdownModal = () => {
    setNewDropdown({ name: "", description: "" });
    setOpenCreateDropdownDialog(true);
  };

  const closeCreateDropdownModal = () => {
    setOpenCreateDropdownDialog(false);
    setNewDropdown({ name: "", description: "" });
  };

  // Create new dropdown
  const handleAddDropdown = async () => {
    if (!newDropdown.name.trim()) return;
    try {
      const created = await dropdownService.createDropdown(
        newDropdown.name.trim(),
        newDropdown.description.trim()
      );
      closeCreateDropdownModal();
      await loadDropdowns();
      if (
        created &&
        typeof created === "object" &&
        "id" in created &&
        typeof (created as { id: unknown }).id === "number"
      ) {
        setSelectedDropdownId((created as Dropdown).id);
      }
    } catch (e: unknown) {
      let msg = "Could not create dropdown";
      if (
        typeof e === "object" &&
        e !== null &&
        "response" in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response
          ?.data?.message === "string"
      ) {
        msg = (e as { response: { data: { message: string } } }).response.data
          .message;
      } else if (e instanceof Error) {
        msg = e.message;
      }
      window.alert(msg);
    }
  };

  // Delete dropdown (with confirmation)
  const handleDeleteDropdown = (id: number, name: string) => {
    setConfirmMessage(
      `Are you sure you want to delete the dropdown "${name}"?`
    );
    setOnConfirm(() => async () => {
      await dropdownService.deleteDropdown(id);
      await loadDropdowns();
      setSelectedDropdownId("");
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  // Delete option (with confirmation)
  const handleDeleteOption = (id: number, label: string) => {
    setConfirmMessage(`Are you sure you want to delete the option "${label}"?`);
    setOnConfirm(() => async () => {
      await dropdownService.deleteOption(id);
      await loadDropdowns();
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  const openAddOptionDialog = () => {
    setEditingOptionId(null);
    setNewOption({ label: "", value: "", DQI_Score: "" });
    setOpenOptionDialog(true);
  };

  const openEditOptionDialog = (opt: DropdownOption) => {
    setEditingOptionId(opt.id);
    setNewOption({
      label: opt.label,
      value: opt.value,
      DQI_Score:
        opt.DQI_Score !== null && opt.DQI_Score !== undefined
          ? String(opt.DQI_Score)
          : "",
    });
    setOpenOptionDialog(true);
  };

  const closeOptionDialog = () => {
    setOpenOptionDialog(false);
    setEditingOptionId(null);
    setNewOption({ label: "", value: "", DQI_Score: "" });
  };

  // Add or update option
  const handleSaveOption = async () => {
    if (!newOption.label.trim() || !newOption.value.trim()) return;

    const dqiTrim = newOption.DQI_Score.trim();
    let dqiNum: number | undefined;
    if (dqiTrim !== "") {
      const n = Number.parseInt(dqiTrim, 10);
      if (!Number.isFinite(n)) {
        window.alert("DQI Score must be a whole number or left empty.");
        return;
      }
      dqiNum = n;
    }

    try {
      if (editingOptionId != null) {
        await dropdownService.updateOption(
          editingOptionId,
          newOption.label.trim(),
          newOption.value.trim(),
          dqiTrim === "" ? null : dqiNum
        );
      } else {
        if (!selectedDropdownId) return;
        await dropdownService.addOption(
          selectedDropdownId as number,
          newOption.label.trim(),
          newOption.value.trim(),
          dqiNum
        );
      }

      closeOptionDialog();
      await loadDropdowns();
    } catch (e: unknown) {
      let msg = "Save failed";
      if (
        typeof e === "object" &&
        e !== null &&
        "response" in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response
          ?.data?.message === "string"
      ) {
        msg = (e as { response: { data: { message: string } } }).response.data
          .message;
      } else if (e instanceof Error) {
        msg = e.message;
      }
      window.alert(msg);
    }
  };

  const selectedDropdown = dropdowns.find((d) => d.id === selectedDropdownId);

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Dropdown Management</span>
      </div>
      <div className="admin-form-root">
        <div className="dropdown-manager-container dataset-container">
        <div className="dropdown-manager-top">
          <Card className="select-dropdown-card">
            <CardContent>
              {ENABLE_DROPDOWN_EDITING && (
                <Box className="select-dropdown-toolbar">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={openCreateDropdownModal}
                    className="add-option-button"
                  >
                    Create New Dropdown
                  </Button>
                </Box>
              )}
              <FormControl fullWidth className="dropdown-selector">
                <InputLabel>Select Dropdown</InputLabel>
                <Select
                  value={selectedDropdownId}
                  label="Select Dropdown"
                  onChange={(e) => setSelectedDropdownId(Number(e.target.value))}
                >
                  {dropdowns.map((dropdown) => (
                    <MenuItem key={dropdown.id} value={dropdown.id}>
                      {dropdown.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </div>

      {/* Show Selected Dropdown */}
      {selectedDropdown ? (
        <Card className="selected-dropdown-card">
          <CardContent>
            <Box className="dropdown-header">
              <Typography variant="h6" className="dropdown-name">
                {selectedDropdown.name}
              </Typography>
              {ENABLE_DROPDOWN_EDITING && (
                <IconButton
                  className="delete-button"
                  onClick={() =>
                    handleDeleteDropdown(
                      selectedDropdown.id,
                      selectedDropdown.name
                    )
                  }
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            {selectedDropdown.description && (
              <Typography variant="body2" className="dropdown-description">
                {selectedDropdown.description}
              </Typography>
            )}

            <Divider className="dropdown-divider" />

            <Box className="options-header">
              <Typography variant="subtitle1" className="options-title">
                Options ({selectedDropdown.options.length})
              </Typography>
              {ENABLE_DROPDOWN_EDITING && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openAddOptionDialog}
                  className="add-option-button"
                >
                  Add Option
                </Button>
              )}
            </Box>

            <List dense className="options-list">
              {selectedDropdown.options.map((opt) => (
                <ListItem key={opt.id} divider className="option-item">
                  <ListItemText
                    primary={`${opt.label} (${opt.value})`}
                    secondary={
                      opt.DQI_Score !== null && opt.DQI_Score !== undefined
                        ? `DQI Score: ${opt.DQI_Score}`
                        : undefined
                    }
                  />
                  {ENABLE_DROPDOWN_EDITING && (
                    <ListItemSecondaryAction>
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IconButton
                          className="edit-button"
                          onClick={() => openEditOptionDialog(opt)}
                          size="small"
                          aria-label={`Edit ${opt.label}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          className="delete-button"
                          onClick={() => handleDeleteOption(opt.id, opt.label)}
                          size="small"
                          aria-label={`Delete ${opt.label}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
              {selectedDropdown.options.length === 0 && (
                <Typography variant="body2" className="empty-options">
                  No options added yet.
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" className="empty-state">
          Select a dropdown to manage.
        </Typography>
      )}

      {ENABLE_DROPDOWN_EDITING && (
        <>
          {/* Create Dropdown Dialog */}
          <Dialog
            open={openCreateDropdownDialog}
            onClose={closeCreateDropdownModal}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Create New Dropdown</DialogTitle>
            <DialogContent>
              <Box className="option-dialog-content">
                <TextField
                  label="Dropdown Name"
                  variant="outlined"
                  size="small"
                  value={newDropdown.name}
                  onChange={(e) =>
                    setNewDropdown({ ...newDropdown, name: e.target.value })
                  }
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Description"
                  variant="outlined"
                  size="small"
                  value={newDropdown.description}
                  onChange={(e) =>
                    setNewDropdown({ ...newDropdown, description: e.target.value })
                  }
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCreateDropdownModal}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => void handleAddDropdown()}
                className="primary-action-btn"
              >
                Add Dropdown
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Option Dialog */}
          <Dialog
            open={openOptionDialog}
            onClose={closeOptionDialog}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {editingOptionId != null ? "Edit Option" : "Add New Option"}
            </DialogTitle>
            <DialogContent>
              <Box className="option-dialog-content">
                <TextField
                  label="Label *"
                  variant="outlined"
                  size="small"
                  value={newOption.label}
                  onChange={(e) =>
                    setNewOption({ ...newOption, label: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="Value *"
                  variant="outlined"
                  size="small"
                  value={newOption.value}
                  onChange={(e) =>
                    setNewOption({ ...newOption, value: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="DQI Score (optional)"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={newOption.DQI_Score}
                  onChange={(e) =>
                    setNewOption({ ...newOption, DQI_Score: e.target.value })
                  }
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeOptionDialog}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveOption}>
                {editingOptionId != null ? "Save" : "Add Option"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent className="confirm-dialog-content">
              <Typography variant="body2" className="confirm-dialog-message">
                {confirmMessage}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button variant="contained" color="error" onClick={() => onConfirm()}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
        </div>
      </div>
    </div>
  );
};

/** React component — Admin Dropdown Manager. */
export default AdminDropdownManager;
